import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ApiService from '@services/api'
import { useNotification } from '@context/NotificationContext'

function getAuditId(result) {
  return result?.audit?.simulationId || result?.audit?.id || result?.audit?.auditId || null
}

function getActionOptions(result) {
  const actions = result?.interpretedActions || []
  return actions.map((action) => ({
    code: action.actionCode,
    name: action.actionName || action.actionCode,
  })).filter((action) => action.code)
}

export default function DeadlineFeedbackCorrectionCard({ result, originalPayload }) {
  const notify = useNotification()
  const navigate = useNavigate()
  const [interpretationIsCorrect, setInterpretationIsCorrect] = useState('')
  const [selectedActionCode, setSelectedActionCode] = useState('')
  const [correctedActionCode, setCorrectedActionCode] = useState('')
  const [comment, setComment] = useState('')
  const [selectedExcerpt, setSelectedExcerpt] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submissionResponse, setSubmissionResponse] = useState(null)

  const auditId = useMemo(() => getAuditId(result), [result])
  const actionOptions = useMemo(() => getActionOptions(result), [result])
  const originalText = originalPayload?.text || ''
  const mustInformCorrection = interpretationIsCorrect === 'false'
  const canSubmit = Boolean(interpretationIsCorrect)
    && selectedActionCode.length > 0
    && selectedExcerpt.trim().length > 0
    && (!mustInformCorrection || correctedActionCode.trim().length > 0)
    && !submitting

  useEffect(() => {
    setInterpretationIsCorrect('')
    setSelectedActionCode('')
    setCorrectedActionCode('')
    setComment('')
    setSelectedExcerpt('')
    setSubmitted(false)
    setSubmissionResponse(null)
  }, [auditId])

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!interpretationIsCorrect) {
      notify.showError('Informe se a interpretação do motor está correta ou errada.')
      return
    }

    if (mustInformCorrection && !correctedActionCode.trim()) {
      notify.showError('Informe a ação correta quando a interpretação estiver errada.')
      return
    }
    if (!selectedActionCode || !selectedExcerpt.trim()) {
      notify.showError('Selecione a ação validada e o trecho que identifica a ação.')
      return
    }

    const payload = {
      auditId,
      publicationId: originalPayload?.publicationId || null,
      processNumber: originalPayload?.processNumber || null,
      originalText,
      predictedActionCode: selectedActionCode,
      correctedActionCode: mustInformCorrection ? correctedActionCode.trim() : selectedActionCode,
      selectedExcerpt: selectedExcerpt.trim(),
      correctionReason: comment.trim() || null,
    }

    setSubmitting(true)
    const response = await ApiService.submitDeadlineFeedback(payload)
    setSubmitting(false)

    if (!response.success) {
      notify.showError(response.error || 'Não foi possível enviar a validação ao DeadlineEngine.')
      return
    }

    setSubmitted(true)
    setSubmissionResponse(response.data || {})
    notify.showSuccess('Validação enviada ao DeadlineEngine.')
  }

  const goToLearning = () => {
    navigate('/deadline-engine/configuracoes?tab=learning')
  }

  const candidateAvailable = Boolean(submissionResponse?.patternCandidateAvailable || submissionResponse?.PatternCandidateAvailable)
  const submissionMessage = submissionResponse?.message || submissionResponse?.Message
  const normalizedExcerpt = submissionResponse?.normalizedExcerpt || submissionResponse?.NormalizedExcerpt

  return (
    <section className="rules-card deadline-result-section deadline-feedback-card">
      <div className="rules-card-header">
        <div>
          <h3>Validar interpretação do motor</h3>
          <p>Informe apenas se a interpretação retornada está correta ou errada. O back-end trata a aprendizagem.</p>
        </div>
        <span className="status-badge is-warning">Feedback humano</span>
      </div>

      <form className="rules-card-body deadline-feedback-form" onSubmit={handleSubmit}>
        <div className="deadline-feedback-context">
          <span>Ações retornadas em interpretedActions</span>
          <strong>{actionOptions.length ? actionOptions.map((action) => action.name).join(' · ') : 'Nenhuma ação retornada'}</strong>
          <small>{auditId ? `Simulação: ${auditId}` : 'Sem simulationId no retorno de auditoria.'}</small>
        </div>

        {submitted ? (
          <div className={`deadline-feedback-success-panel ${candidateAvailable ? 'is-candidate' : ''}`}>
            <strong>{submissionMessage || 'Validação registrada com sucesso.'}</strong>
            <span>{candidateAvailable ? 'O feedback pode gerar ou atualizar um candidato de aprendizado para revisão administrativa.' : 'O feedback foi salvo e ficará disponível no histórico do motor.'}</span>
            {normalizedExcerpt ? <small>Trecho normalizado: {normalizedExcerpt}</small> : null}
            {candidateAvailable ? (
              <button type="button" className="btn btn-outline deadline-feedback-cta" onClick={goToLearning}>
                <i className="fas fa-arrow-right" /> Ir para Aprendizado
              </button>
            ) : null}
          </div>
        ) : null}

        <fieldset className="deadline-feedback-choice-group">
          <legend>A interpretação do motor está correta?</legend>
          <label>
            <input type="radio" name="interpretationIsCorrect" value="true" checked={interpretationIsCorrect === 'true'} onChange={(event) => setInterpretationIsCorrect(event.target.value)} />
            Sim, está correta
          </label>
          <label>
            <input type="radio" name="interpretationIsCorrect" value="false" checked={interpretationIsCorrect === 'false'} onChange={(event) => setInterpretationIsCorrect(event.target.value)} />
            Não, está errada
          </label>
        </fieldset>

        <label className="rules-field">
          <span className="rules-field-label">Qual ação retornada você está validando?</span>
          <select value={selectedActionCode} onChange={(event) => setSelectedActionCode(event.target.value)}>
            <option value="">Selecione uma ação retornada</option>
            {actionOptions.map((action) => <option key={action.code} value={action.code}>{action.name} ({action.code})</option>)}
          </select>
        </label>

        <label className="rules-field">
          <span className="rules-field-label">Trecho que identifica a ação *</span>
          <textarea
            rows="5"
            value={originalText}
            readOnly
            onSelect={(event) => {
              const value = event.currentTarget.value.slice(event.currentTarget.selectionStart, event.currentTarget.selectionEnd).trim()
              if (value) setSelectedExcerpt(value)
            }}
          />
          <textarea rows="3" value={selectedExcerpt} onChange={(event) => setSelectedExcerpt(event.target.value)} placeholder="Selecione o trecho acima ou informe um fragmento da publicação." />
        </label>

        {mustInformCorrection ? (
          <label className="rules-field">
            <span className="rules-field-label">Ação correta *</span>
            <input
              type="text"
              value={correctedActionCode}
              onChange={(event) => setCorrectedActionCode(event.target.value)}
              placeholder="Código da ação correta que o motor deveria ter identificado"
            />
          </label>
        ) : null}

        <label className="rules-field">
          <span className="rules-field-label">Comentário opcional</span>
          <textarea rows="3" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Observação livre para a equipe ou para o motor." />
        </label>

        <div className="deadline-feedback-actions">
          {submitted ? <span className="deadline-feedback-success"><i className="fas fa-check-circle" /> Validação registrada.</span> : <span />}
          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
            {submitting ? 'Enviando...' : 'Enviar validação'}
          </button>
        </div>
      </form>
    </section>
  )
}
